import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Switch, 
  Typography, 
  Collapse, 
  Space, 
  Button, 
  message, 
  Spin, 
  Alert,
  Tooltip,
  Tag,
  Divider
} from 'antd';
import { 
  SaveOutlined, 
  ReloadOutlined, 
  InfoCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Panel } = Collapse;

/**
 * Permission Matrix Component
 * Displays and manages user permissions in a categorized matrix format
 */
const PermissionMatrix = ({ userId, username, userRole, onPermissionsChange }) => {
  const [permissions, setPermissions] = useState({});
  const [availablePermissions, setAvailablePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState(null);

  // Load user permissions and available permissions
  useEffect(() => {
    if (userId) {
      loadData();
    }
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Load user permissions and available permissions in parallel
      const [userPermissionsRes, availablePermissionsRes] = await Promise.all([
        axios.get(`/users/${userId}/permissions`),
        axios.get('/users/permissions/available')
      ]);

      setPermissions(userPermissionsRes.data.data.permissions);
      setAvailablePermissions(availablePermissionsRes.data.data.permissions);
      setHasChanges(false);
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err.response?.data?.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permissionKey, value) => {
    const newPermissions = { ...permissions, [permissionKey]: value };
    setPermissions(newPermissions);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`/users/${userId}/permissions`, {
        permissions: permissions
      });
      
      message.success('Permissions updated successfully');
      setHasChanges(false);
      
      // Notify parent component
      if (onPermissionsChange) {
        onPermissionsChange(permissions);
      }
    } catch (err) {
      console.error('Error saving permissions:', err);
      message.error(err.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await axios.patch(`/users/${userId}/permissions/reset`);
      message.success('Permissions reset to role defaults');
      await loadData(); // Reload to get updated permissions
      
      // Notify parent component
      if (onPermissionsChange) {
        onPermissionsChange(permissions);
      }
    } catch (err) {
      console.error('Error resetting permissions:', err);
      message.error(err.response?.data?.message || 'Failed to reset permissions');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionStatus = (permissionKey) => {
    const hasPermission = permissions[permissionKey];
    return hasPermission ? 'granted' : 'denied';
  };

  const renderPermissionItem = (permissionKey, permissionInfo) => {
    const hasPermission = permissions[permissionKey] || false;
    const status = getPermissionStatus(permissionKey);
    
    return (
      <div key={permissionKey} style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid #f0f0f0'
      }}>
        <div style={{ flex: 1 }}>
          <Space direction="vertical" size={2}>
            <Space>
              <Text strong>{permissionInfo.name}</Text>
              <Tag color={status === 'granted' ? 'green' : 'red'} size="small">
                {status === 'granted' ? 'Granted' : 'Denied'}
              </Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {permissionInfo.description}
            </Text>
          </Space>
        </div>
        <div style={{ marginLeft: '16px' }}>
          <Switch
            checked={hasPermission}
            onChange={(checked) => handlePermissionChange(permissionKey, checked)}
            checkedChildren={<CheckCircleOutlined />}
            unCheckedChildren={<CloseCircleOutlined />}
            disabled={userRole === 'Admin'} // Admin permissions cannot be modified
          />
        </div>
      </div>
    );
  };

  const renderCategory = (categoryName, categoryPermissions) => {
    const permissionCount = Object.keys(categoryPermissions).length;
    const grantedCount = Object.keys(categoryPermissions).filter(
      key => permissions[key]
    ).length;

    return (
      <Panel 
        header={
          <Space>
            <Text strong>{categoryName}</Text>
            <Tag color="blue">{grantedCount}/{permissionCount}</Tag>
          </Space>
        } 
        key={categoryName}
      >
        {Object.entries(categoryPermissions).map(([key, info]) => 
          renderPermissionItem(key, info)
        )}
      </Panel>
    );
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading permissions...</div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          message="Error Loading Permissions"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={loadData}>
              Retry
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            Permission Management
          </Title>
          <Tooltip title="Configure user permissions for different system features">
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        </Space>
      }
      extra={
        <Space>
          {hasChanges && (
            <Text type="warning" style={{ fontSize: '12px' }}>
              Unsaved changes
            </Text>
          )}
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleReset}
            loading={saving}
            disabled={userRole === 'Admin'}
          >
            Reset to Defaults
          </Button>
          <Button 
            type="primary" 
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!hasChanges || userRole === 'Admin'}
          >
            Save Changes
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text strong>User: </Text>
          <Text>{username}</Text>
          <Divider type="vertical" />
          <Text strong>Role: </Text>
          <Tag color="blue">{userRole}</Tag>
          {userRole === 'Admin' && (
            <Tag color="gold">All Permissions</Tag>
          )}
        </div>

        {userRole === 'Admin' ? (
          <Alert
            message="Administrator Account"
            description="Admin users have all permissions by default and cannot be modified."
            type="info"
            showIcon
          />
        ) : (
          <Collapse defaultActiveKey={Object.keys(availablePermissions)}>
            {Object.entries(availablePermissions).map(([category, categoryPermissions]) =>
              renderCategory(category, categoryPermissions)
            )}
          </Collapse>
        )}
      </Space>
    </Card>
  );
};

export default PermissionMatrix;
